import React from 'react';
import { Paper, Box, Button } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import dayjs from 'dayjs';

// ----------------- helpers de tempo -----------------
function getSchedToday(hhmmss) {
  if (!hhmmss) return null;
  const [h='00', m='00', s='00'] = String(hhmmss).split(':');
  const now = dayjs();
  return now.startOf('day').hour(+h||0).minute(+m||0).second(+s||0);
}

// pior atraso visível → 30m / 20m / 10m / 5m / null
function decideWorstLateThreshold(rows) {
  let maxLateSec = -1;
  for (const r of rows) {
    if (!r.isIda) continue;
    if (r.status !== 'Em Atraso') continue;
    if (r.secsToSched == null || r.isStarted) continue;
    const latenessSec = Math.max(0, -r.secsToSched);
    if (latenessSec > maxLateSec) maxLateSec = latenessSec;
  }
  if (maxLateSec < 300) return null;
  if (maxLateSec >= 1800) return '30m';
  if (maxLateSec >= 1200) return '20m';
  if (maxLateSec >= 600)  return '10m';
  return '5m';
}

export default function TripsGridAlerts({
  viagens = [],
  statusDerivado,
  getStatusComIcone,
  pageSize = 10,
  refreshSeq = 0,      // ainda resetamos por segurança
  loading = false,
  tickSeconds = 50,    // intervalo do loop independente
}) {
  // ----------------- referências dos áudios -----------------
  const audio5mRef = React.useRef(null);
  const audio10mRef = React.useRef(null);
  const audio20mRef = React.useRef(null);
  const audio30mRef = React.useRef(null);
  const audioDesvioRef = React.useRef(null);
  const audioQuebraRef = React.useRef(null);

  // estado leve
  const [soundEnabled, setSoundEnabled] = React.useState(
    () => localStorage.getItem('trafego_sound_enabled') === '1'
  );
  const [soundError, setSoundError] = React.useState(false);

  // controle do player
  const playingRef = React.useRef(false);
  const nextAlertAfterPlayRef = React.useRef(null); // 1 pendente no máximo
  const tickerRef = React.useRef(null);

  // rows derivadas para a grade e para a decisão
  const rows = React.useMemo(() => {
    const now = dayjs();
    return (viagens || []).map((v) => {
      const sched = getSchedToday(v?.partidaPlan);
      const rowStatus = statusDerivado ? statusDerivado(v) : v?.status;
      const started = !!(v?.partidaReal && String(v.partidaReal).trim() !== '')
                  || rowStatus === 'Em Andamento'
                  || rowStatus === 'Executada';
      const secsToSched = sched ? sched.diff(now, 'second') : null;

      const isIda = String(v?.trajeto?.sentido || '').toLowerCase() === 'ida';
      const id = `${v.trajeto?.numeroLinha || ''}__${v.nmTabela || ''}__${v.partidaPlan || ''}`;
      return {
        id,
        linha: v.trajeto?.numeroLinha || '',
        tab: v.nmTabela || '',
        horaProg: v.partidaPlan || '',
        horaReal: v.partidaReal || '',
        veiculo: v.veiculoReal || '',
        status: rowStatus,
        secsToSched,
        isStarted: started,
        isIda,
        sched,
      };
    });
  }, [viagens, statusDerivado]);

  // manter rows sempre atuais dentro do setInterval estável
  const rowsRef = React.useRef(rows);
  React.useEffect(() => { rowsRef.current = rows; }, [rows]);

  // desbloqueia autoplay com 1 clique
  const enableSound = async () => {
    setSoundError(false);
    try {
      const el = audio5mRef.current;
      if (el) {
        el.currentTime = 0;
        await el.play();
        el.pause();
        el.currentTime = 0;
      }
      setSoundEnabled(true);
      localStorage.setItem('trafego_sound_enabled', '1');
    } catch (e) {
      setSoundError(true);
    }
  };
  const disableSound = () => {
    setSoundEnabled(false);
    localStorage.setItem('trafego_sound_enabled', '0');
  };

  // tocar um alerta específico (MP3)
  const playAlert = React.useCallback(async (type) => {
    let el = null;
    if (type === '5m') el = audio5mRef.current;
    else if (type === '10m') el = audio10mRef.current;
    else if (type === '20m') el = audio20mRef.current;
    else if (type === '30m') el = audio30mRef.current;
    else if (type === 'desvio') el = audioDesvioRef.current;
    else if (type === 'quebra') el = audioQuebraRef.current;

    if (!el) return;

    // se já estiver tocando, agenda (substitui o agendado anterior)
    if (playingRef.current) {
      nextAlertAfterPlayRef.current = type;
      return;
    }

    try {
      playingRef.current = true;
      el.pause();
      el.currentTime = 0;
      await el.play();

      const onEnded = () => {
        el.removeEventListener('ended', onEnded);
        playingRef.current = false;

        // se há alerta agendado (1 no máximo), toca agora
        if (nextAlertAfterPlayRef.current) {
          const scheduled = nextAlertAfterPlayRef.current;
          nextAlertAfterPlayRef.current = null;
          // pequeno delay para evitar colisão
          setTimeout(() => { playAlert(scheduled); }, 0);
        }
      };
      el.addEventListener('ended', onEnded, { once: true });
    } catch (e) {
      playingRef.current = false;
      setSoundError(true);
    }
  }, []);

  // reset “soft” a cada refresh (opcional)
  React.useEffect(() => {
    nextAlertAfterPlayRef.current = null;
    // não mexe em playingRef: deixa terminar o que estiver tocando
  }, [refreshSeq]);

  // loop independente que repete enquanto houver atraso
  React.useEffect(() => {
    if (!soundEnabled) return;
    if (tickerRef.current) clearInterval(tickerRef.current);

    const tick = () => {
      if (document.hidden) return;             // não tocar com aba oculta
      const type = decideWorstLateThreshold(rowsRef.current);
      if (!type) return;                       // nada a tocar
      playAlert(type);                         // toca ou agenda
    };

    // 1º tick rápido para resposta imediata, depois intervalo regular
    tick();
    tickerRef.current = setInterval(tick, Math.max(5, tickSeconds) * 1000);

    return () => { if (tickerRef.current) clearInterval(tickerRef.current); };
  }, [soundEnabled, tickSeconds, playAlert]);

  // -------- tabela --------
  const fmtHHMM = (t) => {
    if (!t) return '';
    const [h='00', m='00'] = String(t).split(':');
    return `${h.padStart(2,'0')}:${m.padStart(2,'0')}`;
  };

  const columns = [
    { field: 'linha', headerName: 'LINHA', flex: 1, headerAlign: 'center', align: 'center' },
    { field: 'tab',   headerName: 'TABELA', flex: 1, headerAlign: 'center', align: 'center' },
    {
      field: 'horaProg',
      headerName: 'HORA PROG',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      renderCell: (p) => (<Box sx={{ fontWeight: 600 }}>{fmtHHMM(p.value)}</Box>),
    },
    {
      field: 'difTempo',
      headerName: 'DIF. TEMPO',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        // 🔎 pegamos também o status da linha
        const { isStarted, secsToSched, sched, horaReal, status } = params.row;
        const real = horaReal ? getSchedToday(horaReal) : null;

        // ✅ considera "início por status" (mesmo sem horaReal ainda)
        const startedByStatus = status === 'Em Andamento' || status === 'Executada';
        const started = Boolean(isStarted) || startedByStatus;

        // 🛑 já iniciou (pela fonte), mas ainda não temos horaReal → não exibir "+MM:SS" fictício
        if (started && !real) {
          return '';
        }

        // ⏱️ caso iniciado E com horaReal: diferença real vs programado
        if (started && sched && real) {
          const diffSec = real.diff(sched, 'second');
          const mm = String(Math.floor(Math.abs(diffSec) / 60)).padStart(2, '0');
          const ss = String(Math.abs(diffSec) % 60).padStart(2, '0');
          const color = diffSec > 0 ? '#e53935' : undefined;
          return <Box sx={{ fontWeight: 600, color }}>{diffSec >= 0 ? `+${mm}:${ss}` : `-${mm}:${ss}`}</Box>;
        }

        // ⌛ fallback: ainda não iniciou → usa contagem até o horário programado
        if (secsToSched == null) return '';
        const mm = String(Math.floor(Math.abs(secsToSched) / 60)).padStart(2, '0');
        const ss = String(Math.abs(secsToSched) % 60).padStart(2, '0');
        const isWithin5 = secsToSched <= 300 && secsToSched > 0;
        const isLate = secsToSched <= 0;
        const color = isLate ? '#e53935' : isWithin5 ? '#fbc02d' : undefined;
        return <Box sx={{ fontWeight: 600, color }}>{secsToSched > 0 ? `-${mm}:${ss}` : `+${mm}:${ss}`}</Box>;
      }

    },
    { field: 'horaReal', headerName: 'HORA REAL', flex: 1, headerAlign: 'center', align: 'center' },
    { field: 'veiculo',  headerName: 'VEÍCULO',   flex: 1, headerAlign: 'center', align: 'center' },
    {
      field: 'status', headerName: 'SITUAÇÃO', flex: 1, headerAlign: 'center', align: 'center',
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1 }}>
          {getStatusComIcone ? getStatusComIcone(p.value) : p.value}
        </Box>
      ),
    },
  ];

  // constantes para 10 linhas (ajuste se quiser)
  const PAGE_SIZE = pageSize ?? 10; // vem da prop, padrão 10
  const ROW_H = 52;                 // altura de cada linha
  const HEADER_H = 56;              // cabeçalho
  const FOOTER_H = 56;              // rodapé (paginação)
  const GRID_H = HEADER_H + PAGE_SIZE * ROW_H + FOOTER_H;

  return (
    <Paper
      sx={{
        // Removi height fixo (500) e passei a usar minHeight baseado na grade
        minHeight: (theme) => `calc(${GRID_H}px + ${theme.spacing(3)})`, // p:1.5 → 24px
        width: '100%',
        borderRadius: 2,
        backgroundColor: 'background.default',
        boxShadow: 3,
        p: 1.5,
        '& .row-warning': { backgroundColor: 'rgba(251,192,45,0.12)' },
        '& .row-late':    { backgroundColor: 'rgba(229,57,53,0.12)'  },
        '& .MuiDataGrid-columnHeaders': {
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[200],
          color: (theme) => theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
          fontWeight: 'bold',
          fontSize: 14,
        },
        '& .MuiDataGrid-row': {
          backgroundColor: 'background.paper',
          '&:hover': { backgroundColor: 'action.hover' },
        },
        '& .MuiDataGrid-cell': {
          color: 'text.primary',
          fontSize: 14,
          borderBottom: '1px solid #e0e0e0',
        },
        '& .MuiDataGrid-footerContainer': {
          backgroundColor: 'background.paper',
          borderTop: '1px solid #e0e0e0',
        },
      }}
    >
      {/* controles de som */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1, gap: 1 }}>
        {!soundEnabled ? (
          <Button size="small" variant="contained" onClick={enableSound}>
            Ativar Alertas
          </Button>
        ) : (
          <Button size="small" variant="outlined" onClick={disableSound}>
            Silenciar
          </Button>
        )}
        {soundError && (
          <Box sx={{ color: 'error.main', fontSize: 12, alignSelf: 'center' }}>
            Som bloqueado — clique em “Ativar sons”.
          </Box>
        )}
      </Box>

      {/* áudios personalizados */}
      <audio ref={audio5mRef}    preload="auto" src={require('../assets/alert-5m.mp3')} />
      <audio ref={audio10mRef}   preload="auto" src={require('../assets/alert-10m.mp3')} />
      <audio ref={audio20mRef}   preload="auto" src={require('../assets/alert-20m.mp3')} />
      <audio ref={audio30mRef}   preload="auto" src={require('../assets/alert-30m.mp3')} />
      <audio ref={audioDesvioRef} preload="auto" src={require('../assets/alert-desvio.mp3')} />
      <audio ref={audioQuebraRef} preload="auto" src={require('../assets/alert-quebra.mp3')} />

      {/* altura exata da grade: fundo cresce sem cortar os cantos */}
      <Box sx={{ height: GRID_H, width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          initialState={{ pagination: { page: 0, pageSize: 10 } }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          disableSelectionOnClick
          sx={{
            border: '1px solid #01CDFE',
            borderRadius: 2,
            '& .MuiDataGrid-columnHeaders': { borderBottom: '1px solid #E0E0E0' },
            '& .MuiDataGrid-footerContainer': { borderTop: '1px solid #E0E0E0' },
            '& .MuiDataGrid-cell': { borderBottom: 'none' },
          }}
        />
        {/* Se usar MUI v6: troque a linha 'disableSelectionOnClick' por 'disableRowSelectionOnClick' */}

      </Box>
    </Paper>
  );

}
